// Iter57 — Admin SEO Landing Page Editor
// Single-page CMS for one facet's landing page. Sections:
// 1. General (title, banner, hero image, priority, status)
// 2. AI Content (Claude drafts intro + meta; admin per-field approval)
// 3. Manual overrides (always win over AI)
// 4. Featured plans (auto vs manual)
// 5. SEO metadata (canonical, robots, og_image)
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Sparkles, RefreshCw, ArrowLeft, Save, ExternalLink, Star, Check } from "lucide-react";

const AI_FIELDS = [
  { key: "intro",            label: "Introduction",        kind: "text" },
  { key: "meta_title",       label: "SEO Meta Title",      kind: "line" },
  { key: "meta_description", label: "SEO Meta Description", kind: "line" },
];

const AdminLandingPageEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lp, setLp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savingApprovals, setSavingApprovals] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/landing/${id}`);
      setLp(data);
      setPendingApprovals(data.ai_approvals || {});
    } catch (e) {
      toast.error("Failed to load", { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patch = (patchObj) => setLp((prev) => ({ ...prev, ...patchObj }));

  const save = async () => {
    setSaving(true);
    try {
      const seo = lp.seo || {};
      const body = {
        title:                    lp.title,
        banner_title:             lp.banner_title,
        banner_subtitle:          lp.banner_subtitle,
        custom_hero_image:        lp.custom_hero_image,
        featured_mode:            lp.featured_mode,
        featured_plan_ids:        lp.featured_plan_ids,
        manual_intro:             lp.manual_intro,
        manual_meta_title:        lp.manual_meta_title,
        manual_meta_description:  lp.manual_meta_description,
        seo_canonical_url:        seo.canonical_url,
        seo_robots:               seo.robots,
        seo_og_image:             seo.og_image,
        priority:                 lp.priority,
        status:                   lp.status,
      };
      const { data } = await api.patch(`/admin/landing/${id}`, body);
      setLp(data);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const generateAi = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/landing/${id}/generate-ai`, { tone: "editorial" });
      setLp(data);
      setPendingApprovals({});
      toast.success("AI content generated", { description: "Approvals reset — review and tick to publish." });
    } catch (e) {
      toast.error("AI generation failed", { description: e.response?.data?.detail || e.message });
    } finally { setGenerating(false); }
  };

  const saveApprovals = async () => {
    setSavingApprovals(true);
    try {
      const { data } = await api.patch(`/admin/landing/${id}/ai-approvals`, { approvals: pendingApprovals });
      setLp((p) => ({ ...p, ai_approvals: data.approvals }));
      toast.success("Approvals saved");
    } catch (e) {
      toast.error("Approvals failed", { description: e.response?.data?.detail || e.message });
    } finally { setSavingApprovals(false); }
  };

  if (loading) return <AdminLayout title="Loading…"><div className="p-8 text-sm text-muted-foreground">Loading landing page…</div></AdminLayout>;
  if (!lp) return null;

  const approvalsDirty = AI_FIELDS.some((f) => Boolean((lp.ai_approvals || {})[f.key]) !== Boolean(pendingApprovals[f.key]));
  const aiContent = lp.ai_content || {};
  const hasAi = Boolean(aiContent.intro);

  return (
    <AdminLayout title={lp.title || "Landing page"}>
      <div className="max-w-5xl">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <button onClick={() => navigate("/admin/landing")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2" data-testid="landing-back">
              <ArrowLeft size={12} /> Back to Landing Pages
            </button>
            <div className="overline text-primary">{lp.facet_type}</div>
            <h1 className="font-display text-3xl md:text-4xl mt-1">{lp.title}</h1>
            <div className="text-xs font-mono text-muted-foreground mt-1">{lp.url_path}</div>
          </div>
          <div className="flex gap-2">
            <a href={lp.url_path} target="_blank" rel="noreferrer" className="inline-flex items-center px-3 py-2 border border-border text-xs uppercase tracking-[0.12em] hover:border-primary" data-testid="landing-open-public">
              <ExternalLink size={12} className="mr-2" /> Open public
            </a>
            <Button onClick={save} disabled={saving} className="rounded-none bg-primary" data-testid="landing-save">
              {saving ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Save size={14} className="mr-2" />Save</>}
            </Button>
          </div>
        </div>

        {/* ── General ── */}
        <Section title="General">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Title</Label>
              <Input value={lp.title || ""} onChange={(e) => patch({ title: e.target.value })} data-testid="landing-title" />
            </div>
            <div>
              <Label>Priority (1 = hidden, 5 = premium)</Label>
              <div className="flex gap-1 mt-2" data-testid="landing-priority">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => patch({ priority: n })} title={`Priority ${n}`} data-testid={`landing-priority-${n}`}>
                    <Star size={22} className={n <= (lp.priority || 3) ? "fill-amber-500 text-amber-500" : "text-neutral-300 hover:text-amber-400"} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Banner title (optional override)</Label>
              <Input value={lp.banner_title || ""} onChange={(e) => patch({ banner_title: e.target.value })} placeholder={lp.title} data-testid="landing-banner-title" />
            </div>
            <div>
              <Label>Banner subtitle</Label>
              <Input value={lp.banner_subtitle || ""} onChange={(e) => patch({ banner_subtitle: e.target.value })} data-testid="landing-banner-subtitle" />
            </div>
            <div className="md:col-span-2">
              <Label>Custom hero image URL</Label>
              <Input value={lp.custom_hero_image || ""} onChange={(e) => patch({ custom_hero_image: e.target.value })} placeholder="https://…" data-testid="landing-hero-url" />
              <p className="text-[11px] text-muted-foreground mt-1">Leave blank to inherit the facet's default image.</p>
            </div>
            <div>
              <Label>Status</Label>
              <select value={lp.status || "active"} onChange={(e) => patch({ status: e.target.value })} className="w-full h-10 px-3 border border-input bg-background text-sm" data-testid="landing-status">
                <option value="active">Active (indexable)</option>
                <option value="inactive">Inactive (hidden)</option>
              </select>
            </div>
          </div>
        </Section>

        {/* ── AI Content ── */}
        <Section title="AI Content · Draft" right={
          <Button onClick={generateAi} disabled={generating} variant="outline" className="rounded-none" data-testid="landing-ai-generate">
            {generating ? <><Loader2 size={14} className="mr-2 animate-spin" />Generating…</> : <><Sparkles size={14} className="mr-2" />{hasAi ? "Regenerate with AI" : "Generate with AI"}</>}
          </Button>
        }>
          {!hasAi ? (
            <div className="text-sm text-muted-foreground py-6 border border-dashed border-border text-center" data-testid="landing-ai-empty">
              No AI draft yet. Click <strong>Generate with AI</strong> — Claude will draft the intro + meta based on this facet's data.
            </div>
          ) : (
            <>
              {aiContent.plan_count_at_generation != null && (
                <p className="text-[11px] text-muted-foreground mb-3">Generated on {aiContent.generated_at?.slice(0, 10)} with {aiContent.plan_count_at_generation} plan(s) matching this facet · {aiContent.model}</p>
              )}
              <div className="space-y-4">
                {AI_FIELDS.map((f) => (
                  <div key={f.key} className="border border-border p-4 bg-white" data-testid={`landing-ai-field-${f.key}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="overline text-primary">{f.label}</div>
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <Checkbox
                          checked={Boolean(pendingApprovals[f.key])}
                          onCheckedChange={(c) => setPendingApprovals((p) => ({ ...p, [f.key]: Boolean(c) }))}
                          data-testid={`landing-approve-${f.key}`}
                        />
                        <span>Approve for public</span>
                        {(lp.ai_approvals || {})[f.key] && <span className="text-[10px] uppercase tracking-[0.14em] text-emerald-700 ml-1">Live</span>}
                      </label>
                    </div>
                    <div className={`text-sm ${f.kind === "text" ? "whitespace-pre-wrap leading-relaxed" : "font-mono"}`}>
                      {aiContent[f.key] || <span className="italic text-muted-foreground">—</span>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-end">
                <Button onClick={saveApprovals} disabled={savingApprovals || !approvalsDirty} className="rounded-none" data-testid="landing-approvals-save">
                  {savingApprovals ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Check size={14} className="mr-2" />Save approvals{approvalsDirty ? " *" : ""}</>}
                </Button>
              </div>
            </>
          )}
        </Section>

        {/* ── Manual overrides ── */}
        <Section title="Manual Overrides · Always Win Over AI">
          <p className="text-[11px] text-muted-foreground mb-3">Anything you type here appears on the public page instead of the AI draft — even if the AI field is approved.</p>
          <div className="space-y-4">
            <div>
              <Label>Manual intro</Label>
              <Textarea rows={6} value={lp.manual_intro || ""} onChange={(e) => patch({ manual_intro: e.target.value })} data-testid="landing-manual-intro" />
            </div>
            <div>
              <Label>Manual meta title</Label>
              <Input value={lp.manual_meta_title || ""} onChange={(e) => patch({ manual_meta_title: e.target.value })} data-testid="landing-manual-title" />
            </div>
            <div>
              <Label>Manual meta description</Label>
              <Textarea rows={2} value={lp.manual_meta_description || ""} onChange={(e) => patch({ manual_meta_description: e.target.value })} data-testid="landing-manual-desc" />
            </div>
          </div>
        </Section>

        {/* ── Featured Plans ── */}
        <Section title="Featured Plans">
          <div className="space-y-3">
            <div className="flex gap-2">
              {["auto", "manual"].map((m) => (
                <button
                  key={m}
                  onClick={() => patch({ featured_mode: m })}
                  className={`px-3 py-1.5 text-xs uppercase tracking-[0.14em] border ${lp.featured_mode === m ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50"}`}
                  data-testid={`landing-featured-mode-${m}`}
                >
                  {m === "auto" ? "Auto (top plans by views)" : "Manual selection"}
                </button>
              ))}
            </div>
            {lp.featured_mode === "manual" ? (
              <div>
                <Label>Featured plan IDs (one per line, in display order)</Label>
                <Textarea
                  rows={4}
                  value={(lp.featured_plan_ids || []).join("\n")}
                  onChange={(e) => patch({ featured_plan_ids: e.target.value.split(/\s+/).filter(Boolean) })}
                  placeholder="plan_abc123..."
                  className="font-mono text-xs"
                  data-testid="landing-featured-ids"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Paste one plan ID per line. The public page will use this exact order.</p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Top 6 published plans for this facet by featured flag, view count and recency.</p>
            )}
          </div>
        </Section>

        {/* ── SEO ── */}
        <Section title="SEO Metadata">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Canonical URL</Label>
              <Input value={(lp.seo || {}).canonical_url || ""} onChange={(e) => patch({ seo: { ...(lp.seo || {}), canonical_url: e.target.value } })} placeholder={lp.url_path} data-testid="landing-canonical" />
            </div>
            <div>
              <Label>Robots</Label>
              <Input value={(lp.seo || {}).robots || "index,follow"} onChange={(e) => patch({ seo: { ...(lp.seo || {}), robots: e.target.value } })} data-testid="landing-robots" />
            </div>
            <div>
              <Label>Open Graph image</Label>
              <Input value={(lp.seo || {}).og_image || ""} onChange={(e) => patch({ seo: { ...(lp.seo || {}), og_image: e.target.value } })} placeholder="https://…" data-testid="landing-og-image" />
            </div>
          </div>
        </Section>
      </div>
    </AdminLayout>
  );
};

const Section = ({ title, right, children }) => (
  <section className="mt-8 border-t border-border pt-6">
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-display text-xl">{title}</h2>
      {right}
    </div>
    {children}
  </section>
);

export default AdminLandingPageEditor;
